import React from "react";
import DayPicker from "react-day-picker";
import ComponentWrapper from "./ComponentWrapper";
import { useTranslation } from "../../i18n/TranslationProvider";

// Build react-day-picker labels from the browser's Intl API, so every
// supported language works without hard-coded month/weekday lists.
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const buildLocaleProps = (language) => {
  // Jan 2023 starts on a Sunday, so days 1-7 map to Sunday..Saturday
  const monthFmt = new Intl.DateTimeFormat(language, { month: "long" });
  const longFmt = new Intl.DateTimeFormat(language, { weekday: "long" });
  const shortFmt = new Intl.DateTimeFormat(language, { weekday: "short" });
  const narrowFmt = new Intl.DateTimeFormat(language, { weekday: "narrow" });
  const isLatin = (s) => /^[\p{Script=Latin}.]+$/u.test(s);

  const months = Array.from({ length: 12 }, (_, m) =>
    capitalize(monthFmt.format(new Date(2023, m, 1)))
  );
  const weekdaysLong = Array.from({ length: 7 }, (_, d) =>
    capitalize(longFmt.format(new Date(2023, 0, d + 1)))
  );
  const weekdaysShort = Array.from({ length: 7 }, (_, d) => {
    const short = shortFmt.format(new Date(2023, 0, d + 1)).replace(".", "");
    // Latin scripts: 2 letters (Su, Lu, Mi…); others (e.g. Chinese): narrow form
    return isLatin(short)
      ? capitalize(short.slice(0, 2))
      : narrowFmt.format(new Date(2023, 0, d + 1));
  });

  return { months, weekdaysLong, weekdaysShort };
};

const Calendar = ({ selectedDays, onDayClick, modifiers }) => {
  const { t, language } = useTranslation();
  const today = new Date();

  // English uses react-day-picker's built-in defaults
  const localeProps = language === "en" ? {} : buildLocaleProps(language);
  const labels = {
    nextMonth: t("dashboard.ui.calendar.nextMonth"),
    previousMonth: t("dashboard.ui.calendar.previousMonth"),
  };

  return (
    <ComponentWrapper>
      <DayPicker
        modifiers={modifiers}
        onDayClick={onDayClick}
        selectedDays={selectedDays}
        disabledDays={{ after: today }}
        locale={language}
        labels={labels}
        {...localeProps}
      />
    </ComponentWrapper>
  );
};

export default Calendar;